import { deleteAsset, getAsset, getAssetMetasByPrefix, putAsset } from "./db";

/**
 * Bounded opportunistic tile cache — the offline seam from the tiles design
 * (spec §8). Tiles fetched while browsing online are persisted to the
 * existing `assets` store so recently viewed areas survive brief offline
 * windows; it is NOT an offline-pack guarantee (that is v2). Per-revision
 * cap with trim-on-write because the assets store has no global eviction.
 * Rows carry the owning `projectId`, so project unpin
 * (`deleteAssetsByProject`) clears a plan's tiles for free, and the `tile:`
 * prefix keeps them invisible to `planIdFromAssetKey`-based resync logic.
 * See [[offline-asset-cache]].
 */
export const TILE_CACHE_CAP_BYTES = 15 * 1024 * 1024;

export function tileAssetKey(
  revisionId: string,
  level: number,
  x: number,
  y: number,
): string {
  return `tile:${revisionId}:${level}:${x}_${y}`;
}

function revisionPrefix(revisionId: string): string {
  return `tile:${revisionId}:`;
}

// Module-level state, HMR-safe without an `import.meta.hot` guard (unlike
// use-project-plans' window listener): a hot swap just creates fresh maps —
// totals re-seed lazily from one IDB prefix scan and orphaned old chains
// settle harmlessly. Nothing external registers against these.
/** revisionId → running byte total. Lazily seeded from one prefix scan. */
const sizeTotals = new Map<string, number>();
/** Serialize writes per revision so concurrent tile loads can't double-trim. */
const writeChains = new Map<string, Promise<void>>();

export async function getCachedTile(
  revisionId: string,
  level: number,
  x: number,
  y: number,
): Promise<Blob | null> {
  const row = await getAsset(tileAssetKey(revisionId, level, x, y));
  return row?.blob ?? null;
}

export function cacheTile(
  revisionId: string,
  projectId: string,
  level: number,
  x: number,
  y: number,
  blob: Blob,
): Promise<void> {
  const prev = writeChains.get(revisionId) ?? Promise.resolve();
  const next = prev
    .then(() => writeOne(revisionId, projectId, level, x, y, blob))
    .catch(() => {
      // Cache writes are best-effort — a quota error must never break tile
      // display.
    });
  writeChains.set(revisionId, next);
  return next;
}

async function writeOne(
  revisionId: string,
  projectId: string,
  level: number,
  x: number,
  y: number,
  blob: Blob,
): Promise<void> {
  let total = sizeTotals.get(revisionId);
  if (total === undefined) {
    const metas = await getAssetMetasByPrefix(revisionPrefix(revisionId));
    total = metas.reduce((sum, m) => sum + m.sizeBytes, 0);
  }
  await putAsset({
    key: tileAssetKey(revisionId, level, x, y),
    blob,
    contentType: blob.type || "image/jpeg",
    fetchedAt: Date.now(),
    projectId,
  });
  total += blob.size;
  if (total > TILE_CACHE_CAP_BYTES) {
    const metas = (await getAssetMetasByPrefix(revisionPrefix(revisionId))).sort(
      (a, b) => a.fetchedAt - b.fetchedAt,
    );
    let running = metas.reduce((sum, m) => sum + m.sizeBytes, 0);
    for (const meta of metas) {
      if (running <= TILE_CACHE_CAP_BYTES) break;
      await deleteAsset(meta.key);
      running -= meta.sizeBytes;
    }
    total = running;
  }
  sizeTotals.set(revisionId, total);
}

export async function __resetTileCacheForTesting(): Promise<void> {
  sizeTotals.clear();
  writeChains.clear();
  for (const meta of await getAssetMetasByPrefix("tile:")) {
    await deleteAsset(meta.key);
  }
}
